import type { Widget, Flow } from '@dashboard/types/config'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'

/**
 * Sanitizes and validates an embed code shortname
 * Converts to lowercase and replaces invalid characters with hyphens
 * Only allows lowercase letters, numbers, and hyphens
 *
 * @param input - The raw input string
 * @returns Sanitized string matching the pattern: /^[a-z0-9-]+$/
 */
export const sanitizeEmbedCodeShortname = (input: string): string => {
  if (!input) {
    return ''
  }

  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-') // Replace any non-alphanumeric (except hyphens) with hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

/**
 * Validates an embed code shortname
 * Must contain only lowercase letters, numbers, and hyphens
 * Must not be empty
 *
 * @param shortname - The shortname to validate
 * @returns true if valid, false otherwise
 */
export const isValidEmbedCodeShortname = (shortname: string): boolean => {
  if (!shortname || shortname.length === 0) {
    return false
  }
  return /^[a-z0-9-]+$/.test(shortname)
}

/**
 * Generates an initial embed code shortname from a widget name
 * Applies same sanitization rules as user input
 *
 * @param name - The widget name
 * @returns Sanitized shortname safe for embed codes
 */
export const generateEmbedCodeShortname = (name: string): string => sanitizeEmbedCodeShortname(name)

/**
 * Generates the HTML embed code for an inline widget
 *
 * @param shortname - The sanitized embed code shortname
 * @returns HTML embed code string or empty string if shortname is invalid
 */
export const generateEmbedCode = (shortname: string): string => {
  if (!isValidEmbedCodeShortname(shortname)) {
    return ''
  }
  return `<div class="rm-area-${shortname}"></div>`
}

/**
 * Checks if a widget is used by any flows
 *
 * @param widget - The widget to check
 * @returns Array of flows that use this widget
 */
export const widgetUsedBy = (widget: Widget): Flow[] => {
  const flowStore = useFlowStore()
  return flowStore.flows.filter((flow) => flow.id === widget.contentId)
}

/**
 * Checks if a widget is currently in use
 *
 * @param widget - The widget to check
 * @returns true if widget is used by any flow, false otherwise
 */
export const widgetIsInUse = (widget: Widget): boolean => widgetUsedBy(widget).length > 0

/**
 * Gets all widgets that use a specific flow
 *
 * @param flowId - The flow ID to search for
 * @returns Array of widgets using this flow
 */
export const getWidgetsForFlow = (flowId: string): Widget[] => {
  const widgetStore = useWidgetStore()
  return widgetStore.widgets.filter((widget) => widget.contentId === flowId)
}

/**
 * Checks if a widget can be safely deleted
 * A widget can be deleted if it's not the last widget for its flow
 *
 * @param widget - The widget to check
 * @returns Object with canDelete boolean and reason string
 */
export const canDeleteWidget = (widget: Widget): { canDelete: boolean; reason?: string } => {
  const widgetsForFlow = getWidgetsForFlow(widget.contentId!)

  if (widgetsForFlow.length === 1 && widgetIsInUse(widget)) {
    return {
      canDelete: false,
      reason: 'This is the only widget for this flow. Deleting it will make the flow inaccessible.'
    }
  }

  return { canDelete: true }
}

/**
 * Validates widget configuration before saving
 *
 * @param widget - The widget to validate
 * @returns Object with isValid boolean and errors array
 */
export const validateWidget = (widget: Widget): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Validate widget name
  if (!widget.name || widget.name.trim().length === 0) {
    errors.push('Widget name is required')
  }

  // Validate widget type
  const validTypes = ['modal', 'bar', 'toaster', 'inline', 'takeover']
  if (!validTypes.includes(widget.type!)) {
    errors.push('Invalid widget type')
  }

  if (widget.type === 'inline') {
    const hasPlacement = widget.visibility?.placements?.some((rule) => rule.path.trim() && rule.selector.trim())
    const areas = widget.visibility?.areas
    if (!areas?.trim() && !hasPlacement) {
      errors.push('Embedded widgets require a placement or an embed code shortname')
    } else if (areas?.trim() && !isValidEmbedCodeShortname(areas)) {
      errors.push('Embed code shortname must contain only lowercase letters, numbers, and hyphens')
    }
    if (
      widget.visibility?.placements?.some(
        (rule) => rule.domain !== undefined && (!rule.domain.trim() || /[:/\\*?#@\s]/.test(rule.domain))
      )
    ) {
      errors.push('Placement domains must be bare hostnames without a scheme, path, or wildcard')
    }
  }

  // Validate content association
  if (!widget.contentId) {
    errors.push('Widget must be associated with a flow or campaign')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Checks if a widget type supports triggers
 * Inline widgets don't have triggers as they're always visible
 *
 * @param type - The widget type
 * @returns true if widget type supports triggers
 */
export const widgetSupportsTriggersConditions = (type: string): boolean => type !== 'inline'

/**
 * Checks if a widget type supports visibility rules
 *
 * @param type - The widget type
 * @returns true if widget type supports visibility rules
 */
export const widgetSupportsVisibilityRules = (type: string): boolean => true // All widget types support visibility rules

// Valid hostname regex: allows wildcards (*), subdomains, and valid TLDs
// Examples: *.example.com, example.com, sub.example.co.uk
const VALID_HOSTNAME_REGEX = /^(\*\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z]{2,})?$/i

/**
 * Validates a domain value for visibility settings
 *
 * @param domain - The domain value to validate
 * @returns Error message string if invalid, undefined if valid
 */
export const validateVisibilityDomain = (domain: string | undefined): string | undefined => {
  if (!domain || domain === '*') {
    return undefined
  }

  // Check for protocols (including malformed like http:)
  if (/^[a-z]+:/i.test(domain)) {
    return 'Do not include protocol (http:// or https://)'
  }

  // Check for valid hostname format
  if (!VALID_HOSTNAME_REGEX.test(domain)) {
    return 'Enter a valid domain (e.g., example.com or *.example.com)'
  }

  return undefined
}

/**
 * Validates a path value for visibility settings
 *
 * @param path - The path value to validate
 * @returns Error message string if invalid, undefined if valid
 */
export const validateVisibilityPath = (path: string | undefined): string | undefined => {
  if (!path) {
    return undefined
  }

  // Check for leading slash
  if (path.startsWith('/')) {
    return 'Do not start with / - it is assumed'
  }

  // Check for protocols (http://, https://, or malformed like http:)
  if (/^[a-z]+:/i.test(path)) {
    return 'Enter a path, not a full URL'
  }

  // Check for domain pattern
  if (/^[a-z0-9][-a-z0-9]*\.[a-z]{2,}/i.test(path)) {
    return 'Enter a path, not a domain (e.g., "blog" not "example.com")'
  }

  // Check for query parameters
  if (path.includes('?')) {
    return 'Query parameters (?) are not allowed in the path'
  }

  return undefined
}

/**
 * Gets available trigger types for a widget
 *
 * @param widgetType - The widget type
 * @returns Array of trigger type options
 */
export const getAvailableTriggerTypes = (widgetType: string): { label: string; value: string }[] => {
  const baseTriggers = [
    { label: 'Immediately', value: 'immediate' },
    { label: 'Scroll Percentage', value: 'scroll' },
    { label: 'Time On Page', value: 'time' },
    { label: 'Manually', value: 'manual' }
  ]

  // Add widget-specific triggers
  if (widgetType === 'modal') {
    baseTriggers.splice(1, 0, { label: 'Form Submission', value: 'formsubmit' })
    baseTriggers.push({ label: 'Exit Intent', value: 'exit' })
  } else if (widgetType === 'takeover') {
    baseTriggers.push({ label: 'Exit Intent', value: 'exit' })
  } else if (widgetType === 'bar') {
    baseTriggers.push({ label: 'Anchored to top of page', value: 'barstatic' })
  }

  return baseTriggers
}
